import { ApiHttpClient } from "../../common/http/api-http-client";
import { ResourceBaseService } from "../../common/resource-base/resource-base.service";
import { HttpRequest } from "../../common/http/http-request";
import { CreateFileDto } from "../file/create-file.dto";
import { DeleteFileDto } from "../file/delete-file.dto";
import { InvoiceDto } from "@/core/features/invoice/invoice.dto";
import { CreateInvoiceDto } from "@/core/features/invoice/create-invoice.dto";
import { UpdateInvoiceDto } from "@/core/features/invoice/update-invoice.dto";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { IRequest } from "@/interfaces/IRequest";
import { cloneDeep } from "lodash";
import { plainToInstance } from "class-transformer";
import { InvoiceTotalDto } from "@/core/features/invoice/invoice-total.dto";
import { ChangeInvoiceStatusDto } from "@/core/features/invoice/change-invoice-status.dto";
import { InvoiceStatusEnum } from "@altertec_gparn/lib";
import { InvoiceSummaryDto } from "@/core/features/invoice/invoice-summary.dto";
import { SetInvoicesPaymentDateBulkDto } from "@/core/features/invoice/set-invoices-payment-date-bulk.dto";
import { SetInvoicesPaymentDateBulkResult } from "@/core/features/invoice/set-invoices-payment-date-bulk-result.interface";
import { SetInvoicesDeliveredToBulkDto } from "@/core/features/invoice/set-invoices-delivered-to-bulk.dto";
import { SetInvoicesDeliveredToBulkResult } from "@/core/features/invoice/set-invoices-delivered-to-bulk-result.interface";

export class InvoicesResource extends ResourceBaseService<InvoiceDto, CreateInvoiceDto, UpdateInvoiceDto> {
  protected EntityDto = InvoiceDto;
  protected CreateEntityDto = CreateInvoiceDto;
  protected UpdateEntityDto = UpdateInvoiceDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/invoices");
  }

  createManyAttachments(id: string, files: CreateFileDto[]): Promise<InvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "POST",
        auth: true,
        body: { bulk: files },
      })
    );
  }

  deleteAttachment(id: string, deleteFile: DeleteFileDto): Promise<InvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/attachments",
        method: "DELETE",
        auth: true,
        body: deleteFile,
      })
    );
  }

  async findByCertificationId(certificationId: string, request: IRequest): Promise<FindManyResult<InvoiceDto>> {
    const req = cloneDeep(request);
    if (!req.filters) req.filters = {};
    Object.assign(req.filters, { "certificationHasInvoices.certificationId": certificationId });
    if (!req.join) req.join = [];
    if (!req.join.includes("certificationHasInvoices")) req.join.push("certificationHasInvoices");
    return this.findAll(req);
  }

  async importInvoices(file: File): Promise<{ status: "queued"; jobId: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.apiHttpClient.request<FormData, { status: "queued"; jobId: string }>(
      HttpRequest.create<FormData>({
        url: this.resourceUrl + "/import",
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );
  }

  async downloadInvoicesImportTemplate(): Promise<void> {
    return this.downloadExcel(null, "/import/template", "plantilla-facturas");
  }

  async getTotal(request: IRequest): Promise<InvoiceTotalDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(InvoiceTotalDto, result));
  }

  async getSummary(): Promise<InvoiceSummaryDto> {
    return this.apiHttpClient
      .request<void, InvoiceSummaryDto>(
        HttpRequest.create({
          url: this.resourceUrl + "/summary",
          method: "GET",
          auth: true,
        })
      )
      .then((result: InvoiceSummaryDto) =>
        plainToInstance(InvoiceSummaryDto, result, { excludeExtraneousValues: true })
      );
  }

  async downloadIncomeForecastExcel(request: IRequest): Promise<void> {
    return this.downloadExcel(request, "/income-forecast/excel", "prev-ingresos");
  }

  changeStatus(id: string, status: InvoiceStatusEnum): Promise<InvoiceDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${id}/change-status`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(ChangeInvoiceStatusDto, { status, id }),
      })
    );
  }

  async setPaymentDateInBulk(
    invoiceIds: string[],
    paymentDate: string | Date
  ): Promise<SetInvoicesPaymentDateBulkResult> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/bulk/payment-date",
        method: "PATCH",
        auth: true,
        body: plainToInstance(SetInvoicesPaymentDateBulkDto, {
          invoiceIds,
          paymentDate,
        }),
      })
    );
  }

  async setDeliveredToInBulk(invoiceIds: string[], deliveredTo: string): Promise<SetInvoicesDeliveredToBulkResult> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/bulk/delivered-to",
        method: "PATCH",
        auth: true,
        body: plainToInstance(SetInvoicesDeliveredToBulkDto, {
          invoiceIds,
          deliveredTo,
        }),
      })
    );
  }
}
